import { Button } from "@msuite/ui/button";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { Fragment, useState } from "react";
import { Can } from "@/components/citadel";
import { client } from "@/hono";

/** Props Interface */
interface MailboxMenuProps {
	mailboxId: string;
}

export function MailboxMenu({ mailboxId }: MailboxMenuProps) {
	/** State */
	const [isPolling, setIsPolling] = useState(false);

	/** Hooks */
	const { t } = useTranslation();
	const qc = useQueryClient();

	/** Functions */
	// The poll runs on the queue, so the request returns before anything is stored; the email
	// list is refreshed once more after a moment, which is when a small mailbox is usually done.
	async function handleOnPollNow() {
		setIsPolling(true);
		try {
			const response = await client.mailboxes[":mailboxId"].poll.$post({
				param: { mailboxId },
			});
			await responseHandler(response);
			toast.success(t("mailboxes.poll_enqueued"));
			setTimeout(() => {
				void qc.invalidateQueries({ predicate: invalidate(["mailboxes"]) });
			}, 8_000);
		} finally {
			setIsPolling(false);
		}
	}

	/** Render */
	return (
		<Fragment>
			<Can
				action="update"
				subject="Integration"
			>
				<Button
					variant="primary"
					icon={<Icon name="RefreshCw" />}
					onClick={handleOnPollNow}
					isLoading={isPolling}
				>
					{t("mailboxes.poll_now")}
				</Button>
			</Can>
		</Fragment>
	);
}
